'use client';

import { useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { RegistrationAddressFormFields } from '../../../form-controls/Address/RegistrationAddressFormFields';
import { useRegistrationAddressFormMode } from '../../../form-controls/Address/useRegistrationAddressFormMode';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { RegistrationStepLogo } from '../../../Logos/Logos';
import { ContinueButton } from '../components/ContinueButton/ContinueButton';
import { StepLayout } from '../components/StepLayout/StepLayout';
import { useStepValidation, useValidationErrorHandler } from '../hooks/usePlayerValidation';
import type { FormValues } from '../types';
import { transformAddressPayload } from '../utils/addressUtils';

function AddressStep() {
  const t = useTranslations('form');
  const step = useTranslations('register.screens.address');
  const form = useFormContext<FormValues>();
  const { updateFormData, goToNextStep } = useMultistepForm();
  const FORM_ID = 'address-form';
  const isMobile = useIsMobile();
  const lastValidationStatusCode = useRef<number | undefined>(undefined);
  const lastValidationCaughtError = useRef<unknown>(undefined);

  const initialAddressData = form.getValues('address');
  const initialShowManualEntry = !!(initialAddressData?.houseNameNumber || initialAddressData?.streetName || initialAddressData?.city || initialAddressData?.postcode);

  const { showManualEntry, lookupOpen, setLookupOpen, handleAddressSelect, toggleEntryMode } = useRegistrationAddressFormMode({
    form,
    initialShowManualEntry,
  });

  const [dropdownHeight, setDropdownHeight] = useState<number>(0);

  const { validateWithStatus, isValidating: isLoading } = useStepValidation({
    form,
    fieldMapping: {
      streetName: 'address.streetName',
      streetNr: 'address.houseNameNumber',
      city: 'address.city',
      postCode: 'address.postcode',
      state: 'address.state',
    },
    transformPayload: transformAddressPayload,
  });

  const { handleError } = useValidationErrorHandler();

  const toggleAddressEntry = () => {
    toggleEntryMode();
    setDropdownHeight(0);
  };

  const validateAndUpdateData = async () => {
    const isFormValid = await form.trigger('address');

    if (isFormValid) {
      const {
        success: isApiValid,
        statusCode,
        caughtError,
      } = await validateWithStatus({}, undefined, () => {
        const addressData = form.getValues('address');
        updateFormData('address', addressData);
      });

      lastValidationStatusCode.current = statusCode;
      lastValidationCaughtError.current = caughtError;

      return isApiValid;
    }

    return false;
  };

  const handleContinue = async () => {
    if (!showManualEntry) {
      const addressLookupValue = form.getValues('addressLookup');

      if (typeof addressLookupValue !== 'string' || addressLookupValue.trim() === '') {
        form.setError(
          'addressLookup',
          {
            type: 'manual',
            message: t('validation.address.lookup.required'),
          },
          { shouldFocus: true }
        );
        return;
      }
    }

    const isValid = await validateAndUpdateData();
    if (isValid) {
      goToNextStep();
    } else {
      handleError(lastValidationStatusCode.current, lastValidationCaughtError.current);
    }
  };

  const hideHeader = isMobile && !showManualEntry && lookupOpen;

  return (
    <StepLayout showLogo={false} data-testid="address-step">
      <div className={`transition-all duration-300 ease-in-out ${hideHeader ? 'opacity-0 max-h-0 overflow-hidden -mt-8' : 'opacity-100 max-h-32'}`}>
        <RegistrationStepLogo className="rounded-sm md:hidden mb-4" />
        <h4 className="md:heading-3">{step('title')}</h4>
      </div>

      <div className="flex flex-col gap-8 align-center">
        <RegistrationAddressFormFields
          formIdPrefix={FORM_ID}
          showManualEntry={showManualEntry}
          onLookupOpenChange={setLookupOpen}
          onAddressSelect={handleAddressSelect}
          onToggleEntryMode={toggleAddressEntry}
          enterManuallyLabel={step('enterManually')}
          switchToSearchLabel={step('search')}
          lookupVariant="registration"
          onDropdownHeightChange={setDropdownHeight}
        />
      </div>

      {lookupOpen && <div style={{ height: dropdownHeight }} className="-mt-8" data-testid="address-lookup-dropdown-spacer"></div>}

      <div className="mt-auto md:mt-0" data-track-container="Actions">
        <ContinueButton loading={isLoading} onClick={handleContinue} data-track-name="Continue" />
      </div>
    </StepLayout>
  );
}

export { AddressStep };
